import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Box,
    styled,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import { useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator } from '@mui/icons-material';
import { RankingRow } from './RankingRow';
import { AddNewRankingRow } from './AddNewRankingRow';
import { EmptyRankingState } from './EmptyRankingState';

interface RankingTableProps {
    onRowSelect: (rankingIndex: number, selected: boolean) => void;
    rankings: Models.IngestConfigApi.ForcedRankings.Ranking[];
    selectedRows: number[];
    filteringPartialProductSpecs: Record<string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec>;
    defaultPartialProductSpecs: Record<string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec>;
    onFilteringOptionChange: (rankingIndex: number, specId: string | null) => void;
    onDefaultOptionChange: (rankingIndex: number, specId: string | null) => void;
    onImportCsv: (csvContent: string) => Promise<void>;
    onAddRanking: (ranking: Models.IngestConfigApi.ForcedRankings.Ranking) => void;
    onDeleteRanking: (rankingIndex: number) => void;
    showAddRow: boolean;
    onShowAddRow: () => void;
    onHideAddRow: () => void;
    onReorderRankings: (oldIndex: number, newIndex: number) => void;
    onSelectAll: (selected: boolean) => void;
    productKey: string;
    cultures: string[];
    hasCultureSelected: boolean;
    duplicateRankingsMap: Map<number, number[]>;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const AddNewRankingTableRow = styled(TableRow)(() => ({
    backgroundColor: '#e3f2fd', // Light blue for the add new ranking row
}));

const StyledBox = styled(Box)(({ theme }) => ({
    width: theme.spacing(8),
    textAlign: 'left',
}));

const SortableItem = ({
    id,
    children,
    isDuplicate,
}: {
    id: string;
    children: React.ReactNode;
    isDuplicate: boolean;
}): JSX.Element => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 999 : 'auto',
    };

    return (
        <StyledTableRow
            ref={setNodeRef}
            style={style}
            sx={isDuplicate ? { backgroundColor: (theme) => alpha(theme.palette.error.main, 0.2) } : undefined}
        >
            <TableCell
                padding="none"
                width="40px"
                {...attributes}
                {...listeners}
                sx={{
                    cursor: 'grab',
                    color: isDragging ? 'primary.main' : 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                    display: 'table-cell',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: '16px',
                    height: '100%',
                }}
            >
                <DragIndicator />
            </TableCell>
            {children}
        </StyledTableRow>
    );
};

export const RankingTable = ({
    onRowSelect,
    rankings,
    selectedRows,
    filteringPartialProductSpecs,
    defaultPartialProductSpecs,
    onFilteringOptionChange,
    onDefaultOptionChange,
    onImportCsv,
    onAddRanking,
    onDeleteRanking,
    showAddRow,
    onShowAddRow,
    onHideAddRow,
    onReorderRankings,
    onSelectAll,
    productKey,
    cultures,
    hasCultureSelected,
    duplicateRankingsMap,
}: RankingTableProps): JSX.Element => {
    const filteringOptionGroupOptions = useMemo(
        () => Object.entries(filteringPartialProductSpecs)
            .sort(([, a], [, b]) => a.name.localeCompare(b.name)),
        [filteringPartialProductSpecs],
    );

    const defaultOptionGroupOptions = useMemo(
        () => Object.entries(defaultPartialProductSpecs)
            .sort(([, a], [, b]) => a.name.localeCompare(b.name)),
        [defaultPartialProductSpecs],
    );

    const handleAddRanking = (ranking: Models.IngestConfigApi.ForcedRankings.Ranking): void => {
        onAddRanking(ranking);
        onHideAddRow();
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor),
    );

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = parseInt(active.id as string, 10);
            const newIndex = parseInt(over.id as string, 10);

            if (oldIndex !== -1 && newIndex !== -1) {
                onReorderRankings(oldIndex, newIndex);
            }
        }
    };

    const allSelected = rankings.length > 0 && selectedRows.length === rankings.length;
    const someSelected = selectedRows.length > 0 && selectedRows.length < rankings.length;

    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        onSelectAll(event.target.checked);
    };

    return (
        <TableContainer component={Paper}>
            <DndContext
                collisionDetection={closestCenter}
                sensors={sensors}
                onDragEnd={handleDragEnd}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="none" width="40px">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        paddingX: 4,
                                    }}
                                >
                                    <DragIndicator color="disabled" />
                                </Box>
                            </TableCell>
                            <TableCell sx={{ paddingY: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title="Select multiple rows to change their filtering or default option groups at once">
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={someSelected}
                                            onChange={handleSelectAllChange}
                                        />
                                    </Tooltip>
                                    <StyledBox>
                                        {selectedRows.length > 0 && (
                                            <Typography color="text.secondary" variant="caption">
                                                (
                                                {selectedRows.length}
                                                )
                                            </Typography>
                                        )}
                                    </StyledBox>
                                </Box>
                            </TableCell>
                            <TableCell>Rank</TableCell>
                            <TableCell>Preview</TableCell>
                            <TableCell>Concept ID</TableCell>
                            <TableCell>Filtering Option Group</TableCell>
                            <TableCell>Default Option Group</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rankings.length === 0 && !showAddRow && (
                            <StyledTableRow>
                                <TableCell colSpan={8}>
                                    <EmptyRankingState
                                        hasCultureSelected={hasCultureSelected}
                                        onImportCsv={onImportCsv}
                                        onShowAddRow={onShowAddRow}
                                    />
                                </TableCell>
                            </StyledTableRow>
                        )}
                        {showAddRow && (
                            <AddNewRankingTableRow>
                                <AddNewRankingRow
                                    cultures={cultures}
                                    currentRankedConceptIds={rankings.map((r) => r.designConceptId)}
                                    defaultOptionGroupOptions={defaultOptionGroupOptions}
                                    filteringOptionGroupOptions={filteringOptionGroupOptions}
                                    productKey={productKey}
                                    onAdd={handleAddRanking}
                                    onHide={onHideAddRow}
                                />
                            </AddNewRankingTableRow>
                        )}
                        <SortableContext
                            items={rankings.map((_, index) => index.toString())}
                            strategy={verticalListSortingStrategy}
                        >
                            {rankings.map((ranking, index) => {
                                // Create a stable key from ranking properties
                                const stableKey = `${ranking.designConceptId}-${ranking.filteringProductSpecId || 'no-filter'}-${ranking.defaultProductSpecId || 'no-default'}-${ranking.rank}`;
                                const duplicateRanks = duplicateRankingsMap.get(index);

                                return (
                                    <SortableItem
                                        id={index.toString()}
                                        isDuplicate={!!duplicateRanks}
                                        key={stableKey}
                                    >
                                        <RankingRow
                                            conceptId={ranking.designConceptId}
                                            cultures={cultures}
                                            defaultOptionGroup={ranking.defaultProductSpecId}
                                            defaultOptionGroupOptions={defaultOptionGroupOptions}
                                            duplicateRanks={duplicateRanks}
                                            filteringOptionGroup={ranking.filteringProductSpecId}
                                            filteringOptionGroupOptions={filteringOptionGroupOptions}
                                            productKey={productKey}
                                            rank={ranking.rank}
                                            selected={selectedRows.includes(index)}
                                            onDefaultOptionChange={(value): void => onDefaultOptionChange(
                                                index,
                                                value,
                                            )}
                                            onDelete={(): void => {
                                                onDeleteRanking(index);
                                            }}
                                            onFilteringOptionChange={(value): void => onFilteringOptionChange(
                                                index,
                                                value,
                                            )}
                                            onSelect={(selected): void => onRowSelect(
                                                index,
                                                selected,
                                            )}
                                        />
                                    </SortableItem>
                                );
                            })}
                        </SortableContext>
                    </TableBody>
                </Table>
            </DndContext>
        </TableContainer>
    );
};
